/**
 * TabOnboardingOverlay
 *
 * A single-screen overlay that introduces users to the 5 main navigation tabs
 * after they complete the onboarding flow. Shows once per user.
 *
 * KEY IMPLEMENTATION NOTES:
 * - Overlay stops above navigation bar using buildPageSpacing(spacing.giga, 'BOTTOM')
 *   which accounts for safe area insets (home indicator on iPhone)
 * - 5 labels must match 5 navigation tabs exactly using flex: 1 for each
 * - No horizontal padding on labels container to match nav bar edge-to-edge layout
 * - Labels positioned 16px from bottom of overlay (spacing.deci)
 * - 80% black opacity (rgba(0, 0, 0, 0.8)) for strong contrast
 * - Instruction text uses fontStyles.horse (1.16rem) for prominence
 * - Controlled by localStorage key 'hasSeenTabOnboarding'
 * - Only shows on pages with navigation bar (excludes /onboarding, /welcome, /payment, /result)
 */

import React, { useEffect, useState, useRef } from 'react';
import { css } from '@emotion/react';
import { colors, fontStyles, spacing, buildPageSpacing } from '../../styles';

interface TabOnboardingOverlayProps {
  onDismiss: () => void;
}

// Animation timing constants
const FADE_IN_DELAY = 100; // ms - delay before fade in starts
const FADE_OUT_DELAY = 300; // ms - delay to allow fade out animation

const TabOnboardingOverlay: React.FC<TabOnboardingOverlayProps> = ({ onDismiss }) => {
  const [visible, setVisible] = useState(false);
  const dismissTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Fade in animation
    const fadeInTimer = setTimeout(() => setVisible(true), FADE_IN_DELAY);

    return () => {
      clearTimeout(fadeInTimer);
      // Clean up dismiss timer if component unmounts before it fires
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
    };
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    dismissTimerRef.current = setTimeout(onDismiss, FADE_OUT_DELAY);
  };

  const overlayStyles = css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: ${buildPageSpacing(spacing.giga, 'BOTTOM')}; /* Stops above navigation bar, accounts for safe area */
    z-index: 102; /* Above NavigationBar (z-index: 101) */
    background-color: rgba(0, 0, 0, 0.8);
    opacity: ${visible ? 1 : 0};
    transition: opacity 0.3s ease-in-out;
    pointer-events: auto;
  `;

  const instructionTextStyles = css`
    ${fontStyles.horse};
    color: ${colors.sugar};
    text-align: center;
    opacity: 0.9;
    position: absolute;
    top: 40%;
    left: 0;
    right: 0;
    transform: translateY(-50%);
  `;

  const labelsContainerStyles = css`
    position: absolute;
    bottom: ${spacing.deci}; /* 16px from bottom of overlay */
    left: 0;
    right: 0;
    width: 100%;
    display: flex;
    align-items: flex-end;
    pointer-events: none;
  `;

  const labelStyles = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
  `;

  const labelTextStyles = css`
    ${fontStyles.mouse};
    color: ${colors.sugar};
    text-align: center;
    margin-bottom: ${spacing.milli};
    font-weight: 500;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  `;

  const arrowStyles = css`
    color: ${colors.sugar};
    font-size: 28px;
    line-height: 1;
  `;

  const tabs = [
    { label: 'Track\nperformance', key: 'portfolios' },
    { label: 'Spot\nwinners', key: 'ideas' },
    { label: 'Stay up\nto date', key: 'markets' },
    { label: 'Ask\nanything', key: 'chat' },
    { label: 'Configure\noptions', key: 'more' },
  ];

  return (
    <div css={overlayStyles} onClick={handleDismiss}>
      <div css={instructionTextStyles}>Tap anywhere to continue</div>
      <div css={labelsContainerStyles}>
        {tabs.map((tab) => (
          <div key={tab.key} css={labelStyles}>
            <div css={labelTextStyles}>
              {tab.label.split('\n').map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
            <div css={arrowStyles}>↓</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TabOnboardingOverlay;
